import { getAuth } from 'firebase/auth';
import { SupportedLanguages } from '../i18n/languageConfig';
import { getFirestore, collection, query, orderBy, getDocs } from 'firebase/firestore';

interface DreamAnalysisResponse {
  analysis: string;
  keywords: string[];
  serenityScore: number;
  summary?: string;
}

interface SaveDreamResponse {
  success?: boolean;
  dreamId?: string;
  name?: string;  // ID complet renvoyé par Firestore (fallback)
  createTime?: string;
}

interface Approaches {
  categoryId: string;
  categoryName: string;
  subcategoryId: string;
  subcategoryName: string;
}

const WORKER_BASE_URL = 'https://onyros-api.atelier-davai.workers.dev';

function addLanguagePrefix(prompt: string, language: SupportedLanguages): string {
  // TODO: implement languagePromptPrefixes and responseFormats
  // return `${languagePromptPrefixes[language]}\n${prompt}\n${responseFormats[language]}`;
  return prompt;
}

// Fonction utilitaire pour normaliser les textes
const normalizeText = (text: string): string => {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Supprimer les accents
    .trim();
};

// Fonction pour récupérer les mots-clés récurrents des rêves précédents
export const getRecurringKeywords = async (userId: string, maxKeywords: number = 5): Promise<string[]> => {
  try {
    console.log(`[getRecurringKeywords] Récupération des mots-clés récurrents pour l'utilisateur ${userId}`);
    
    const db = getFirestore();
    const dreamsRef = collection(db, `users/${userId}/dreams`);
    const dreamsQuery = query(dreamsRef, orderBy('createdAt', 'desc'));
    const dreamsSnapshot = await getDocs(dreamsQuery);
    
    // Limiter aux 10 derniers rêves
    const recentDreams = dreamsSnapshot.docs.slice(0, 10);
    console.log(`[getRecurringKeywords] Nombre de rêves récents trouvés: ${recentDreams.length}`);
    
    // Extraire tous les mots-clés des rêves récents
    const keywordsCount = new Map<string, number>();
    recentDreams.forEach(doc => {
      const dreamData = doc.data();
      if (dreamData.keywords && Array.isArray(dreamData.keywords)) {
        console.log(`[getRecurringKeywords] Rêve ${doc.id}: ${dreamData.keywords.length} mots-clés trouvés`);
        dreamData.keywords.forEach((keyword: string) => {
          keywordsCount.set(keyword, (keywordsCount.get(keyword) || 0) + 1);
        });
      } else {
        console.log(`[getRecurringKeywords] Rêve ${doc.id}: aucun mot-clé trouvé`);
      }
    });
    
    console.log(`[getRecurringKeywords] Nombre total de mots-clés uniques: ${keywordsCount.size}`);
    
    // Filtrer les mots-clés qui apparaissent plus d'une fois
    const recurringKeywords = Array.from(keywordsCount.entries())
      .filter(([_, count]) => count >= 2)
      .map(([keyword]) => keyword)
      .slice(0, maxKeywords);
    
    console.log(`[getRecurringKeywords] Mots-clés récurrents identifiés: ${recurringKeywords.join(', ')}`);
    
    return recurringKeywords;
  } catch (error) {
    console.error('[getRecurringKeywords] Erreur lors de la récupération des mots-clés récurrents:', error);
    return [];
  }
};

export const analyzeDream = async (
  dreamContent: string,
  emotions: string | null,
  language: SupportedLanguages,
  prompt: string,
  realLifeInfoPonctuel?: string,
  realLifeInfoPermanent?: string
): Promise<DreamAnalysisResponse> => {
  try {
    // Récupérer l'utilisateur actuel
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    // Récupérer les mots-clés récurrents si un utilisateur est connecté
    let recurringKeywords: string[] = [];
    if (currentUser) {
      console.log(`[analyzeDream] Récupération des mots-clés récurrents pour l'utilisateur ${currentUser.uid}`);
      recurringKeywords = await getRecurringKeywords(currentUser.uid);
    }
    
    // Ajouter une instruction pour éviter les thèmes récurrents
    let avoidRecurringThemes = '';
    if (recurringKeywords.length > 0) {
      console.log(`[analyzeDream] Ajout de l'instruction d'évitement pour ${recurringKeywords.length} mots-clés récurrents`);
      
      if (language === 'fr') {
        avoidRecurringThemes = `\nÉvite de te concentrer sur ces aspects déjà explorés dans les analyses précédentes: ${recurringKeywords.join(', ')}.`;
      } else if (language === 'en') {
        avoidRecurringThemes = `\nAvoid focusing on these aspects already explored in previous analyses: ${recurringKeywords.join(', ')}.`;
      } else if (language === 'es') {
        avoidRecurringThemes = `\nEvita centrarte en estos aspectos ya explorados en análisis anteriores: ${recurringKeywords.join(', ')}.`;
      }
      
      console.log(`[analyzeDream] Instruction d'évitement: "${avoidRecurringThemes}"`);
    } else {
      console.log(`[analyzeDream] Aucun mot-clé récurrent trouvé, pas d'instruction d'évitement ajoutée`);
    }
    
    // Construction du système de prompt
    const defaultPrompt = `
      Tu es un analyste de rêves expert.
      ${prompt}
      ${avoidRecurringThemes}

      Données d'entrée :
      - Description du rêve : ${dreamContent}
      - Émotions ressenties : ${emotions || "Non spécifiées"}
      - Informations ponctuelles sur la vie réelle : ${realLifeInfoPonctuel || "Non spécifiées"}
      - Informations permanentes sur la vie réelle : ${realLifeInfoPermanent || "Non spécifiées"}
    `;
    
    const systemPrompt = prompt ? addLanguagePrefix(prompt, language) : defaultPrompt;
    
    const requestBody = {
      dream_text: dreamContent,
      emotions: emotions,
      language: language,
      prompt: systemPrompt,
      realLifeInfoPonctuel: realLifeInfoPonctuel,
      realLifeInfoPermanent: realLifeInfoPermanent
    };

    console.log('Envoi de la requête au Worker:', {
      url: `${WORKER_BASE_URL}/analyze`,
      body: requestBody
    });

    const response = await fetch(`${WORKER_BASE_URL}/analyze`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      mode: 'cors',
      body: JSON.stringify(requestBody)
    });

    console.log('Réponse du Worker:', {
      status: response.status,
      ok: response.ok,
      headers: [...response.headers.entries()]
    });

    if (!response.ok) {
      let errorMessage = 'Erreur lors de l\'analyse du rêve';
      try {
        const errorData = await response.json();
        console.error('Erreur API détaillée:', errorData);
        errorMessage = errorData.message || errorMessage;
      } catch (e) {
        console.error('Erreur brute:', response.statusText);
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Erreur lors de l\'analyse:', error);
    if (error instanceof Error) {
      throw new Error(`Erreur: ${error.message}`);
    }
    throw new Error('Impossible d\'analyser le rêve pour le moment. Veuillez réessayer plus tard.');
  }
}

export async function saveDream(
  dreamText: string,
  emotions: string | null,
  analysis: DreamAnalysisResponse,
  approaches: Approaches[],
  realLifeInfoPonctuel: string,
): Promise<SaveDreamResponse> {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('Utilisateur non connecté');
    }

    const idToken = await user.getIdToken();

    // Normaliser les mots-clés
    const normalizedKeywords = analysis.keywords.map(keyword => normalizeText(keyword));

    // Normaliser les émotions si elles existent
    const normalizedEmotions = emotions
      ? emotions.split(',')
          .map(emotion => normalizeText(emotion))
          .join(',')
      : null;

    const response = await fetch(`${WORKER_BASE_URL}/api/dreams/save`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      mode: 'cors',
      body: JSON.stringify({
        dream_text: dreamText,
        emotions: normalizedEmotions,
        analysis: analysis.analysis,
        keywords: normalizedKeywords,
        serenityScore: analysis.serenityScore,
        summary: analysis.summary ?? '',
        approaches: approaches,
        realLifeInfoPonctuel: realLifeInfoPonctuel,
      })
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la sauvegarde du rêve');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Erreur lors de la sauvegarde:', error);
    if (error instanceof Error) {
      throw new Error(`Erreur: ${error.message}`);
    }
    throw new Error('Impossible de sauvegarder le rêve pour le moment. Veuillez réessayer plus tard.');
  }
}
